import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Facebook, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/LanguageContext";
import { NandaSaMark } from "@/components/BrandMarks";

const NewsletterInline = () => {
  const { locale } = useLanguage();
  const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);
  const [email, setEmail] = useState("");
  const onSubmit = (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error(l("Email invalide", "Invalid email", "Email inválido"));
      return;
    }
    toast.success(l("Merci. Vous êtes inscrit·e.", "Thank you. You are subscribed.", "Obrigado. Está inscrito."));
    setEmail("");
  };
  return (
    <form onSubmit={onSubmit} className="flex items-end gap-3 border-b border-nanda-ink/20 focus-within:border-nanda-ink transition-colors" data-testid="footer-newsletter">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={l("votre@email.com", "your@email.com", "o.seu@email.com")}
        className="flex-1 min-w-0 bg-transparent py-3 outline-none text-nanda-ink placeholder:text-nanda-muted text-sm"
        data-testid="footer-newsletter-input"
      />
      <button type="submit" className="label-overline text-nanda-ink hover:text-nanda-terracotta py-3 whitespace-nowrap transition-colors duration-300" data-testid="footer-newsletter-submit">
        {l("S'abonner", "Subscribe", "Subscrever")} →
      </button>
    </form>
  );
};

const Col = ({ title, children }) => (
  <div>
    <div className="label-overline text-nanda-muted mb-5">{title}</div>
    <ul className="space-y-3 text-sm">{children}</ul>
  </div>
);

const Item = ({ to, testId, children }) => (
  <li>
    <Link className="text-nanda-inkSoft hover:text-nanda-ink transition-colors duration-300" to={to} data-testid={testId}>
      {children}
    </Link>
  </li>
);

// Footer, architecture secondaire complète.
// Le header reste volontairement minimal (4 univers + À propos + Contact) ;
// tout le reste du site reste accessible ici, organisé par famille.
export default function Footer() {
  const year = new Date().getFullYear();
  const { locale } = useLanguage();
  const l = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);

  return (
    <footer data-testid="site-footer" className="border-t border-nanda-line bg-nanda-bgAlt/60">
      <div className="mx-auto max-w-7xl px-6 sm:px-12 py-16 md:py-24 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-x-8 gap-y-12">
        {/* Marque */}
        <div className="col-span-2 md:col-span-3 lg:col-span-4">
          <div className="flex items-center gap-3">
            <NandaSaMark size={30} className="text-nanda-ink" />
            <div className="font-sans text-base font-semibold tracking-[0.28em] uppercase text-nanda-ink">NANDA SÁ</div>
          </div>
          <p className="mt-6 max-w-xs text-nanda-inkSoft text-sm leading-relaxed">
            {l(
              "Une marque. Plusieurs savoir-faire. Énergie solaire, cosmétiques naturels, produits du terroir et formation, en Guinée-Bissau et au Sénégal.",
              "One brand. Several crafts. Solar energy, natural cosmetics, terroir products and training, in Guinea-Bissau and Senegal.",
              "Uma marca. Vários saberes. Energia solar, cosméticos naturais, produtos do terroir e formação, na Guiné-Bissau e no Senegal."
            )}
          </p>
          <div className="mt-8 flex items-center gap-5">
            <a href="https://www.instagram.com/nandasa_africa?igsh=MTVnY3VvY2pkYzM1Zw%3D%3D&utm_source=qr" target="_blank" rel="noopener noreferrer" aria-label="Instagram NANDA SÁ" className="text-nanda-inkSoft hover:text-nanda-ink transition-colors" data-testid="footer-instagram">
              <Instagram size={18} strokeWidth={1.5} />
            </a>
            <a href="https://www.linkedin.com/company/nanda-s%C3%A1/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn NANDA SÁ" className="text-nanda-inkSoft hover:text-nanda-ink transition-colors" data-testid="footer-linkedin">
              <Linkedin size={18} strokeWidth={1.5} />
            </a>
            <a href="https://www.facebook.com/share/1DQsYahN4c/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" aria-label="Facebook NANDA SÁ" className="text-nanda-inkSoft hover:text-nanda-ink transition-colors" data-testid="footer-facebook">
              <Facebook size={18} strokeWidth={1.5} />
            </a>
            <a href="https://wa.me/245956828351" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp NANDA SÁ" className="text-nanda-inkSoft hover:text-nanda-ink transition-colors" data-testid="footer-whatsapp">
              <MessageCircle size={18} strokeWidth={1.5} />
            </a>
          </div>
        </div>

        {/* Univers */}
        <div className="lg:col-span-2">
          <Col title={l("Univers", "Universes", "Universos")}>
            <Item to="/marques/energia-pro" testId="footer-univers-energie">{l("PRO Énergie", "PRO Energy", "PRO Energia")}</Item>
            <Item to="/cosmetiques" testId="footer-univers-cosmetiques">{l("Cosmétiques", "Cosmetics", "Cosméticos")}</Item>
            <Item to="/terroirs" testId="footer-univers-terroirs">{l("Produits & Terroirs", "Products & Terroirs", "Produtos & Terroirs")}</Item>
            <Item to="/marques/academie" testId="footer-univers-academie">{l("Académie", "Academy", "Academia")}</Item>
          </Col>
        </div>

        {/* Maison */}
        <div className="lg:col-span-2">
          <Col title={l("La Maison", "The House", "A Casa")}>
            <Item to="/maison" testId="footer-maison">{l("À propos", "About", "Sobre")}</Item>
            <Item to="/marques" testId="footer-brands">{l("Marques", "Brands", "Marcas")}</Item>
            <Item to="/impact">{l("Impact", "Impact", "Impacto")}</Item>
            <Item to="/engagement-odd" testId="footer-engagement-odd">{l("Engagement ODD", "SDG commitment", "Compromisso ODS")}</Item>
            <Item to="/rejoindre">{l("Nous rejoindre", "Join us", "Junte-se a nós")}</Item>
          </Col>
        </div>

        {/* Ressources */}
        <div className="lg:col-span-2">
          <Col title={l("Ressources", "Resources", "Recursos")}>
            <Item to="/carnet">{l("Carnet", "Journal", "Caderno")}</Item>
            <Item to="/actualites">{l("Actualités", "News", "Actualidades")}</Item>
            <Item to="/tresors">{l("Trésors", "Treasures", "Tesouros")}</Item>
            <Item to="/mediatheque" testId="footer-mediatheque">{l("Médiathèque", "Media library", "Mediateca")}</Item>
            <Item to="/telecharger">{l("Téléchargements", "Downloads", "Descargas")}</Item>
          </Col>
        </div>

        {/* Professionnels */}
        <div className="col-span-2 md:col-span-1 lg:col-span-2">
          <Col title={l("Professionnels", "Professionals", "Profissionais")}>
            <Item to="/devis">{l("Demander un devis", "Request a quote", "Solicitar orçamento")}</Item>
            <Item to="/opportunites" testId="footer-opportunites">{l("Opportunités", "Opportunities", "Oportunidades")}</Item>
            <Item to="/secteurs" testId="footer-sectors">{l("Secteurs", "Sectors", "Sectores")}</Item>
            <Item to="/partenaires">{l("Partenaires", "Partners", "Parceiros")}</Item>
            <Item to="/matieres-premieres-naturelles">{l("Matières premières", "Raw materials", "Matérias-primas")}</Item>
            <Item to="/refrigeration-climatisation">{l("Réfrigération & froid", "Refrigeration & cooling", "Refrigeração & frio")}</Item>
            <Item to="/conseil-implantation">{l("Conseil & implantation", "Consulting & setup", "Consultoria & implantação")}</Item>
            <Item to="/produits-consommables" testId="footer-produits-consommables">{l("Consommables pro", "Professional supplies", "Consumíveis pro")}</Item>
          </Col>
        </div>
      </div>

      {/* Contact + Newsletter */}
      <div className="border-t border-nanda-line">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 py-12 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <div className="label-overline text-nanda-muted mb-5">{l("Nos implantations", "Our locations", "As nossas implantações")}</div>
            <div className="space-y-8 text-sm">
              <div data-testid="footer-implant-senegal">
                <Link to="/ziguinchor" className="label-overline text-nanda-ink mb-3 inline-block hover:text-nanda-terracotta transition-colors" data-testid="footer-implant-senegal-link">Sénégal</Link>
                <div className="text-nanda-inkSoft">Ziguinchor, Sénégal</div>
                <a href="https://wa.me/221710090410" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-nanda-ink hover:text-nanda-terracotta transition-colors" data-testid="footer-phone-sn">
                  {l("Tél. / WhatsApp :", "Phone / WhatsApp:", "Tel. / WhatsApp:")} +221 71 009 04 10
                </a>
              </div>
              <div data-testid="footer-implant-guinee-bissau">
                <Link to="/canchungo" className="label-overline text-nanda-ink mb-3 inline-block hover:text-nanda-terracotta transition-colors" data-testid="footer-implant-guinee-bissau-link">Guinée-Bissau</Link>
                <div className="text-nanda-inkSoft">Canchungo, Région de Cacheu, Guinée-Bissau</div>
                <a href="https://wa.me/245956828351" target="_blank" rel="noopener noreferrer" className="mt-2 inline-block text-nanda-ink hover:text-nanda-terracotta transition-colors" data-testid="footer-phone-gw">
                  {l("Tél. / WhatsApp :", "Phone / WhatsApp:", "Tel. / WhatsApp:")} +245 956 828 351
                </a>
              </div>
              <div>
                <a href="mailto:hello@nanda-sa.com" className="text-nanda-ink hover:text-nanda-terracotta transition-colors" data-testid="footer-email-link">hello@nanda-sa.com</a>
              </div>
            </div>
          </div>
          <div>
            <div className="label-overline text-nanda-muted mb-5">Newsletter</div>
            <p className="text-nanda-inkSoft text-sm leading-relaxed mb-4">{l("Recevez nos lettres trimestrielles.", "Receive our quarterly letters.", "Receba as nossas cartas trimestrais.")}</p>
            <NewsletterInline />
          </div>
        </div>
      </div>

      <div className="border-t border-nanda-line">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 label-overline text-nanda-muted">
          <div className="flex flex-wrap items-center gap-5">
            <span>© {year} NANDA SÁ</span>
            <Link to="/legal/mentions" className="hover:text-nanda-ink transition-colors" data-testid="footer-legal-mentions">{l("Mentions légales", "Legal notice", "Menções legais")}</Link>
            <Link to="/legal/confidentialite" className="hover:text-nanda-ink transition-colors" data-testid="footer-legal-privacy">{l("Politique de confidentialité", "Privacy policy", "Política de privacidade")}</Link>
            <Link to="/legal/cookies" className="hover:text-nanda-ink transition-colors">Cookies</Link>
          </div>
          <span>{l("Une marque. Plusieurs savoir-faire.", "One brand. Several crafts.", "Uma marca. Vários saberes.")}</span>
        </div>
      </div>
    </footer>
  );
}
